import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/i18nContext';
import { useTheme } from '../context/ThemeContext';
import { isAgent } from '../constants/roles';
import AppHeader from '../components/AppHeader';
import ProfileDrawer from '../components/ProfileDrawer';
import '../styles/AdminEntity.css';

// Csak olvasható szerződéslista agenteknek és ügyfeleknek.
// A szűkítést a backend végzi: staff mindent lát, az ügyfél csak a sajátját.
// Szerkeszteni kizárólag admin tud, az /admin/contracts oldalon.

const fromCsv = (csv) => (csv ? String(csv).split(',').map(Number) : []);
const dateOnly = (v) => (v ? String(v).substring(0, 10) : '');

const ContractsViewPage = () => {
  const { api, user, logout } = useAuth();
  const { t } = useI18n();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);

  const staff = isAgent(user?.role);
  const isDark = theme === 'dark';

  // t() nem interpolál — a {{var}} helyettesítés hívási helyen történik
  const tf = (key, vars) =>
    Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{{${k}}}`, v), t(key));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/contracts');
      setContracts(r.data);
    } catch { setError(t('contracts.loadFailed')); }
    finally { setLoading(false); }
  }, [api, t]);

  useEffect(() => { load(); }, [load]);

  const filtered = contracts.filter(c =>
    [c.contract_number, c.customer_username, c.customer_email, c.property_name]
      .join(' ').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={isDark ? 'theme-dark' : 'theme-light'}>
      <AppHeader
        user={user}
        onLogout={logout}
        onOpenProfile={() => setProfileDrawerOpen(true)}
        onBack={() => navigate('/dashboard')}
      />

      <div className="ae-page" style={{ paddingTop: 'calc(var(--app-header-h, 60px) + 1.5rem)' }}>
        {error && <div className="ae-msg error">{error}</div>}

        <div className="ae-card ae-header-card">
          <div className="ae-topbar">
            <h1 className="ae-title">{staff ? t('contracts.title') : t('contracts.mine')}</h1>
          </div>
          <div className="ae-filters">
            <input className="ae-search" placeholder={t('contracts.search')}
                   value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="ae-card">
          {loading ? (
            <div className="ae-empty">{t('common.loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="ae-empty">{t('contracts.none')}</div>
          ) : (
            <div className="ae-table-wrap">
              <table className="ae-table">
                <thead>
                  <tr>
                    <th>{t('contracts.number')}</th>
                    {staff && <th>{t('contracts.customer')}</th>}
                    <th>{t('contracts.property')}</th>
                    <th>{t('contracts.status')}</th>
                    <th>{t('contracts.term')}</th>
                    <th>{t('contracts.sectionVisits')}</th>
                    <th>{t('contracts.feeAmount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id}>
                      <td className="ae-name">{c.contract_number}</td>
                      {staff && <td>{c.customer_username || c.customer_email}</td>}
                      <td>{c.property_name || '—'}</td>
                      <td>
                        <span className={`ae-badge ${c.status === 'active' ? 'active' : 'inactive'}`}>
                          {t(`contracts.statuses.${c.status}`)}
                        </span>
                      </td>
                      <td>{dateOnly(c.start_date)} → {dateOnly(c.end_date) || t('contracts.openEnded')}</td>
                      <td>
                        {tf('contracts.weekly', { count: c.visits_per_week })}
                        {c.visit_weekdays && (
                          <span style={{ color: 'var(--text-muted)' }}>
                            {' '}({fromCsv(c.visit_weekdays).map(d => t(`contracts.weekdays.${d}`)).join(', ')})
                          </span>
                        )}
                      </td>
                      <td>{c.fee_amount ? `${c.fee_amount} ${c.fee_currency}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {profileDrawerOpen && user && (
        <ProfileDrawer userId={user.id} onClose={() => setProfileDrawerOpen(false)} />
      )}
    </div>
  );
};

export default ContractsViewPage;
